import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtTotals, fmtMoney, STATUS_META, formatAxiosError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Plus, Search, Copy, ChevronRight, Trash2 } from "lucide-react";

/** « à signer / disponible le <date> / signé / en échec » — dossier.sepa (déjà booléen serveur). */
function sepaLabel(sepa) {
  if (sepa.done) return "signé";
  if (sepa.failed) return "en échec";
  if (sepa.available_at && new Date(sepa.available_at) > new Date()) return `disponible le ${new Date(sepa.available_at).toLocaleDateString("fr-FR")}`;
  return "à signer";
}

function DossierBadge({ dossier, id }) {
  if (!dossier) return null;
  const hasDocs = dossier.documents.required.length > 0;
  const hasSepa = dossier.sepa.required;
  const hasPhone = !!dossier.phone?.required;
  if (!hasDocs && !hasSepa && !hasPhone) return null;
  if (dossier.complete) {
    return <span data-testid={`partner-dossier-badge-${id}`} className="text-[11px] px-1.5 py-0.5 rounded-sm font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">Dossier complet</span>;
  }
  const present = dossier.documents.required.length - dossier.documents.missing.length;
  const parts = [
    hasDocs && `Pièces ${present}/${dossier.documents.required.length}`,
    hasSepa && `Mandat : ${sepaLabel(dossier.sepa)}`,
    hasPhone && !dossier.phone.done && "RIO manquant",
  ].filter(Boolean);
  if (!parts.length) return null; // ex. RIO reçu, offre pas encore signée : rien à signaler
  return (
    <span data-testid={`partner-dossier-badge-${id}`} className="text-[11px] px-1.5 py-0.5 rounded-sm font-semibold border bg-amber-50 text-amber-700 border-amber-200">
      {parts.join(" · ")}
    </span>
  );
}

export default function PartnerProposals() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { api.get("/partner/proposals").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  const filtered = (items || []).filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return p.title?.toLowerCase().includes(s) || (p.client_name || "").toLowerCase().includes(s);
  });

  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success("Lien copié"); };

  // Comme le détail : jamais une signée (bouton masqué, 409 côté serveur) ; pièces effacées avec la proposition.
  const remove = async (p) => {
    const linkNote = p.status === "brouillon" ? "" : " Le lien envoyé au client ne fonctionnera plus.";
    if (!window.confirm(`Supprimer définitivement cette proposition ?${linkNote}`)) return;
    setDeletingId(p.id);
    try {
      await api.delete(`/partner/proposals/${p.id}`);
      setItems((list) => list.filter((x) => x.id !== p.id));
      toast.success("Proposition supprimée");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setDeletingId(null); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-5xl" data-testid="partner-proposals-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Portail partenaire</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Mes propositions</h1>
        </div>
        <Button onClick={() => navigate("/partner/new")} data-testid="partner-new-proposal-button" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvelle proposition
        </Button>
      </div>

      <div className="relative max-w-sm mb-6">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" data-testid="partner-proposal-search" className="pl-9 rounded-sm" />
      </div>

      {items === null ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-sm px-6 py-16 text-center text-muted-foreground" data-testid="empty-partner-proposals">
          Aucune proposition. Créez votre première proposition.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.brouillon;
            return (
              <div key={p.id} className="bg-card border border-border rounded-sm p-4 sm:p-5" data-testid={`partner-proposal-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <button onClick={() => navigate(`/partner/proposals/${p.id}`)} className="min-w-0 text-left flex-1">
                    <p className="font-medium flex items-center gap-2 flex-wrap">
                      {p.title}
                      <span className={`text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`}>{meta.label}</span>
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">{p.client_name || "Sans client"}</p>
                  </button>
                  <button onClick={() => navigate(`/partner/proposals/${p.id}`)} data-testid={`partner-proposal-open-${p.id}`} className="p-1.5 text-muted-foreground hover:text-primary transition-colors shrink-0">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="font-mono text-sm font-medium">{fmtTotals(p.total_one_shot, p.total_recurring, p.currency)}</span>
                  <DossierBadge dossier={p.dossier} id={p.id} />
                  {p.partner_pricing && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-sm font-semibold border bg-primary/10 text-primary border-primary/20" data-testid={`partner-margin-${p.id}`}>
                      Marge : {fmtMoney(p.partner_pricing.recurring)}/mois + {fmtMoney(p.partner_pricing.one_shot)}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-border">
                  <button onClick={() => copyLink(p.public_url)} data-testid={`partner-copy-link-${p.id}`}
                    className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors">
                    <Copy className="w-3.5 h-3.5" /> Copier le lien
                  </button>
                  <button onClick={() => navigate(`/partner/proposals/${p.id}`)} data-testid={`partner-detail-link-${p.id}`}
                    className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors">
                    Ouvrir le détail
                  </button>
                  {p.status !== "acceptée" && (
                    <button onClick={() => remove(p)} disabled={deletingId === p.id} data-testid={`partner-proposal-delete-${p.id}`}
                      className="ml-auto flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50">
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
